import { useCallback, useRef, useState } from 'react';

const useLongPress = (
  onLongPress: (e: React.TouchEvent) => void,
  {
    shouldPreventDefault = true,
    delay = 300,
  }: { shouldPreventDefault?: boolean; delay?: number } = {}
) => {
  const timeout = useRef<NodeJS.Timeout | null>(null);
  const target = useRef<EventTarget | null>(null);
  const [longPressTriggered, setLongPressTriggered] = useState(false);
  const start = useCallback(
    (event: React.TouchEvent) => {
      if (shouldPreventDefault && event.target) {
        event.target.addEventListener('touchend', preventDefault, {
          passive: false,
        });
        target.current = event.target;
      }
      timeout.current = setTimeout(() => {
        onLongPress(event);
        setLongPressTriggered(true);
      }, delay);
    },
    [onLongPress, delay, shouldPreventDefault]
  );

  const clear = useCallback(
    () => {
      if (timeout && timeout.current) {
        clearTimeout(timeout.current);
      }
      setLongPressTriggered(false);
      if (shouldPreventDefault && target.current) {
        target.current.removeEventListener('touchend', preventDefault);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [shouldPreventDefault, longPressTriggered]
  );

  return {
    onTouchStart: (e: React.TouchEvent) => {
      if (e.touches.length === 2) {
        clear();
      }
      return start(e);
    },
    onTouchMove: clear,
    onTouchEnd: clear,
    onTouchCancel: clear,
  };
};

const isTouchEvent = (event: Event): event is TouchEvent => {
  return 'touches' in event;
};

const preventDefault = (event: Event) => {
  if (!isTouchEvent(event)) return;

  if (
    event.touches.length < 2 &&
    event.preventDefault &&
    (event.target as HTMLElement).id === 'mapbox-map'
  ) {
    event.preventDefault();
  }
};

export default useLongPress;
